// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Bell,
  Hourglass,
  Building2,
  GitBranch,
  KeyRound,
  Laptop,
  Terminal,
  Wrench,
  CreditCard,
  ShieldCheck,
} from 'lucide-react';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { PageHeader } from '@/components/greenlight/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AgentSetup } from '@/components/settings/AgentSetup';
import { PluginSignIns } from '@/components/settings/PluginSignIns';
import { PoliciesSettings } from '@/components/settings/PoliciesSettings';
import { DangerZone } from '@/components/settings/DangerZone';
import {
  IdentitySettings,
  type IdentityCandidate,
  type IdentityVerification,
  type ReconfigureIdentityInput,
} from '@/components/settings/IdentitySettings';
import { BillingSettings } from '@/components/settings/BillingSettings';
import { MaintenanceSettings } from '@/components/settings/MaintenanceSettings';
import { NotificationsSettings } from '@/components/settings/NotificationsSettings';
import { AppLifecycleSettings } from '@/components/settings/AppLifecycleSettings';
import { OrgSettings } from '@/components/settings/OrgSettings';
import {
  SettingsNav,
  type SettingsNavItem,
  type SettingsSectionKey,
} from '@/components/settings/SettingsNav';
import {
  SourceControlSettings,
  type VerifyRepoAccessResult,
} from '@/components/settings/SourceControlSettings';
import { MarketplaceCard } from '@/components/settings/MarketplaceCard';
import type {
  BootstrapMarketplace,
  UnregisterMarketplace,
} from '@/components/greenlight/marketplace/useMarketplaceEditor';
import type {
  MigrationExecuteResult,
  MigrationPreviewResult,
} from '@/components/settings/SourceControlMigration';
import { api } from '@/lib/api';
import { DOCS_LINKS } from '@/lib/docsLinks';
import { submitManifestIfPresent } from '@/lib/githubManifest';
import { useRefetchOnFocus } from '@/lib/useRefetchOnFocus';
import { useCurrentUser } from '@/lib/auth';
import type {
  InstallContact,
  MarketplaceBootstrapOutcome,
  SetCursorAdminKey,
  SetSlackWebhook,
  UpdateNotificationsSettings,
  UpdateAppLifecycleSettings,
} from '@greenlight/shared';

const MARKETPLACE_OUTCOME_MESSAGE: Record<MarketplaceBootstrapOutcome, string> = {
  created: 'Agent plugin repo created.',
  recreated: 'Agent plugin repo recreated.',
  up_to_date: 'Agent plugin repo is already up to date.',
  stale: 'Agent plugin repo found; its contents will update automatically.',
};

export function SettingsPage(): React.JSX.Element {
  usePageTitle('Settings');
  const user = useCurrentUser();
  const isAdmin = user?.role === 'org_admin';
  const isIT = isAdmin || user?.role === 'admin';

  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = api.settings.get.useQuery(['settings'], undefined, {
    queryKey: ['settings'],
  });
  const settings = data?.status === 200 ? data.body : undefined;

  useRefetchOnFocus(
    React.useCallback(
      () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
      [queryClient],
    ),
  );

  const updateOrg = api.settings.updateOrg.useMutation();
  const updateContact = api.settings.updateContact.useMutation();
  const updateAppDiscoverability = api.settings.updateAppDiscoverability.useMutation();
  const updateFeedbackSharing = api.settings.updateFeedbackSharing.useMutation();
  const updateCrashLogSharing = api.settings.updateCrashLogSharing.useMutation();
  const updateObserverReports = api.settings.updateFeedbackObserverReports.useMutation();
  const updateTokenUsageReports = api.settings.updateAgentTokenUsageReports.useMutation();
  const setCursorAdminKey = api.settings.setCursorAdminKey.useMutation();
  const deleteCursorAdminKey = api.settings.deleteCursorAdminKey.useMutation();
  const testIdentity = api.settings.testIdentity.useMutation();
  const updateIdentity = api.settings.updateIdentity.useMutation();
  const updateGroupNameResolution = api.settings.updateGroupNameResolution.useMutation();
  const updateNotifications = api.settings.updateNotifications.useMutation();
  const setSlackWebhook = api.settings.setSlackWebhook.useMutation();
  const deleteSlackWebhook = api.settings.deleteSlackWebhook.useMutation();
  const updateAppLifecycle = api.settings.updateAppLifecycle.useMutation();
  const verifySourceControl = api.settings.verifySourceControl.useMutation();
  const recreateGithubApp = api.settings.recreateGithubApp.useMutation();
  const previewScmMigration = api.settings.previewScmMigration.useMutation();
  const executeScmMigration = api.settings.executeScmMigration.useMutation();
  const bootstrapMarketplace = api.settings.bootstrapPluginMarketplace.useMutation();
  const unregisterMarketplace = api.settings.unregisterPluginMarketplace.useMutation();
  const notificationQueueRef = React.useRef<Promise<void>>(Promise.resolve());

  const runMutation = async (
    label: string,
    fn: () => Promise<{ status: number; body?: unknown }>,
  ): Promise<boolean> => {
    try {
      const res = await fn();
      if (res.status >= 300) throw res;
      toast.success(`${label} updated.`);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      return true;
    } catch (err) {
      // Surface the server's reason when the error body carries one
      // (docs/16 error shape: { code, message, … }).
      const body = (err as { body?: { message?: unknown } } | null)?.body;
      const detail = typeof body?.message === 'string' ? ` ${body.message}` : '';
      toast.error(`Couldn't update ${label.toLowerCase()}.${detail}`);
      return false;
    }
  };

  const onSaveDisplayName = async (display_name: string): Promise<void> => {
    await runMutation('Organization', () => updateOrg.mutateAsync({ body: { display_name } }));
  };

  const onSaveContact = async (contact: InstallContact): Promise<void> => {
    await runMutation('Install contact', () => updateContact.mutateAsync({ body: contact }));
  };

  const onToggleDiscoverabilityDefault = async (
    discoverable_by_default: boolean,
  ): Promise<void> => {
    const succeeded = await runMutation('Default app access', () =>
      updateAppDiscoverability.mutateAsync({ body: { discoverable_by_default } }),
    );
    if (!succeeded) throw new Error('Discoverability default toggle failed');
  };

  const onToggleFeedbackAutoShare = async (auto_share: boolean): Promise<void> => {
    const succeeded = await runMutation('Platform feedback', () =>
      updateFeedbackSharing.mutateAsync({ body: { auto_share } }),
    );
    if (!succeeded) throw new Error('Feedback auto-share toggle failed');
  };

  const onToggleCrashLogAutoShare = async (auto_share: boolean): Promise<void> => {
    const succeeded = await runMutation('App crash logs', () =>
      updateCrashLogSharing.mutateAsync({ body: { auto_share } }),
    );
    if (!succeeded) throw new Error('Crash-log auto-share toggle failed');
  };

  const onToggleObserverReports = async (enabled: boolean): Promise<void> => {
    const succeeded = await runMutation('Agent reports', () =>
      updateObserverReports.mutateAsync({ body: { enabled } }),
    );
    if (!succeeded) throw new Error('Observer reports toggle failed');
  };

  const onToggleTokenUsageReports = async (enabled: boolean): Promise<void> => {
    const succeeded = await runMutation('Build token usage', () =>
      updateTokenUsageReports.mutateAsync({ body: { enabled } }),
    );
    if (!succeeded) throw new Error('Token usage reports toggle failed');
  };

  // Rethrow so the row keeps the form open; runMutation toasts Cursor's reason.
  const onSetCursorAdminKey = async (input: SetCursorAdminKey): Promise<void> => {
    const succeeded = await runMutation('Cursor Admin API key', () =>
      setCursorAdminKey.mutateAsync({ body: input }),
    );
    if (!succeeded) throw new Error('Cursor Admin API key was not saved');
  };

  const onRemoveCursorAdminKey = async (): Promise<void> => {
    const succeeded = await runMutation('Cursor Admin API key', () =>
      deleteCursorAdminKey.mutateAsync({}),
    );
    if (!succeeded) throw new Error('Cursor Admin API key was not removed');
  };

  const onTestConnection = async (): Promise<IdentityVerification | null> => {
    try {
      const res = await testIdentity.mutateAsync({ body: {} });
      if (res.status !== 200) throw res;
      toast.success(res.body.message);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      return { ok: res.body.ok, message: res.body.message, checks: res.body.checks };
    } catch (err) {
      // Surface the server's reason when present (docs/16 error shape), as
      // runMutation does — a failed identity test is exactly when the detail
      // (bad endpoint, expired secret) matters most.
      const body = (err as { body?: { message?: unknown } } | null)?.body;
      const detail = typeof body?.message === 'string' ? ` ${body.message}` : '';
      toast.error(`Couldn't test the identity connection.${detail}`);
      return null;
    }
  };

  const onVerifyIdp = async (candidate: IdentityCandidate): Promise<IdentityVerification> => {
    try {
      const res = await testIdentity.mutateAsync({ body: candidate });
      if (res.status !== 200) throw res;
      return { ok: res.body.ok, message: res.body.message, checks: res.body.checks };
    } catch (err) {
      const body = (err as { body?: { message?: unknown } } | null)?.body;
      const detail =
        typeof body?.message === 'string'
          ? body.message
          : 'Could not reach the issuer. Check the URL and try again.';
      return { ok: false, message: detail };
    }
  };

  const onReconfigure = async (
    body: ReconfigureIdentityInput,
  ): Promise<{ ok: boolean; error?: string }> => {
    try {
      const res = await updateIdentity.mutateAsync({ body });
      if (res.status >= 300) throw res;
      toast.success('Identity provider updated. All sessions were signed out.');
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      return { ok: true };
    } catch (err) {
      const errBody = (err as { body?: { message?: unknown } } | null)?.body;
      const message =
        typeof errBody?.message === 'string'
          ? errBody.message
          : "Couldn't save the identity configuration.";
      toast.error(message);
      return { ok: false, error: message };
    }
  };

  const onToggleGroupNameResolution = async (resolve_group_names: boolean): Promise<void> => {
    const succeeded = await runMutation('Group names', () =>
      updateGroupNameResolution.mutateAsync({ body: { resolve_group_names } }),
    );
    if (!succeeded) throw new Error('Group name resolution toggle failed');
  };

  // ── Source-control lifecycle (docs/07 §Re-install semantics) ──────────────
  const serverMessage = (err: unknown, fallback: string): string => {
    const body = (err as { body?: { message?: unknown } } | null)?.body;
    return typeof body?.message === 'string' ? body.message : fallback;
  };
  const serverCode = (err: unknown): string | undefined => {
    const body = (err as { body?: { code?: unknown } } | null)?.body;
    return typeof body?.code === 'string' ? body.code : undefined;
  };

  const onVerifyRepoAccess = async (): Promise<VerifyRepoAccessResult> => {
    try {
      const res = await verifySourceControl.mutateAsync({ body: {} });
      if (res.status !== 200) throw res;
      return { ok: res.body.ok, accessible: res.body.accessible, missing: res.body.missing };
    } catch (err) {
      return {
        ok: false,
        accessible: [],
        missing: [],
        error: serverMessage(err, "Couldn't reach the GitHub App installation."),
      };
    }
  };

  const onRecreateGithubApp = async (): Promise<void> => {
    try {
      const res = await recreateGithubApp.mutateAsync({ body: {} });
      if (res.status !== 200) throw res;
      // Hand the server-issued manifest back to GitHub in a new tab — this page
      // stays put; the refocus listener below picks up the re-created App.
      submitManifestIfPresent(res.body);
    } catch (err) {
      toast.error(serverMessage(err, "Couldn't start GitHub App re-creation."));
    }
  };

  const onPreviewMigration = async (): Promise<MigrationPreviewResult> => {
    try {
      const res = await previewScmMigration.mutateAsync({ body: {} });
      if (res.status !== 200) throw res;
      return { ok: true, preview: res.body };
    } catch (err) {
      return {
        ok: false,
        code: serverCode(err),
        message: serverMessage(err, "Couldn't check migration status."),
      };
    }
  };

  const onExecuteMigration = async (target_org_handle: string): Promise<MigrationExecuteResult> => {
    try {
      const res = await executeScmMigration.mutateAsync({ body: { target_org_handle } });
      if (res.status !== 200) throw res;
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      return { ok: true, result: res.body };
    } catch (err) {
      return {
        ok: false,
        code: serverCode(err),
        message: serverMessage(err, "Couldn't complete the migration."),
      };
    }
  };

  const onBootstrapMarketplace: BootstrapMarketplace = async (body) => {
    // Failures rethrow without a toast — the shared editor renders them inline
    // next to the form, and a toast would surface the same message twice.
    const res = await bootstrapMarketplace.mutateAsync({ body });
    if (res.status !== 200) throw res;
    toast.success(MARKETPLACE_OUTCOME_MESSAGE[res.body.outcome]);
    await queryClient.invalidateQueries({ queryKey: ['settings'] });
    return res.body;
  };

  const onUnregisterMarketplace: UnregisterMarketplace = async (marketplaceId) => {
    // Same rethrow-without-toast convention as bootstrap: the confirm dialog
    // renders the failure inline.
    const res = await unregisterMarketplace.mutateAsync({ params: { id: marketplaceId } });
    if (res.status !== 200) throw res;
    toast.success('Agent plugin repo unregistered.');
    await queryClient.invalidateQueries({ queryKey: ['settings'] });
    return res.body;
  };

  const onSetSlackWebhook = async (input: SetSlackWebhook): Promise<void> => {
    // Rethrows rather than only toasting: the inline form renders the failure
    // next to the field the person is still looking at.
    const res = await setSlackWebhook.mutateAsync({ body: input });
    if (res.status !== 200) throw res;
    toast.success('Slack webhook saved.');
    await queryClient.invalidateQueries({ queryKey: ['settings'] });
  };

  const onRemoveSlackWebhook = async (): Promise<void> => {
    // Rethrows like onSetSlackWebhook: the confirm drawer renders the failure.
    const res = await deleteSlackWebhook.mutateAsync({});
    if (res.status !== 200) throw res;
    toast.success('Slack webhook removed.');
    await queryClient.invalidateQueries({ queryKey: ['settings'] });
  };

  const onUpdate = (patch: UpdateNotificationsSettings): Promise<void> => {
    const run = async (): Promise<void> => {
      const ok = await runMutation('Notifications', () =>
        updateNotifications.mutateAsync({ body: patch }),
      );
      if (!ok) throw new Error('notifications update failed');
    };

    const queued = notificationQueueRef.current.then(run, run);
    notificationQueueRef.current = queued.catch(() => undefined);
    return queued;
  };

  const onUpdateAppLifecycle = async (patch: UpdateAppLifecycleSettings): Promise<void> => {
    await runMutation('Unused apps', () => updateAppLifecycle.mutateAsync({ body: patch }));
  };

  const ALL_SECTIONS = [
    { key: 'agent' as const, label: 'Agent setup', icon: Terminal, show: true },
    { key: 'sign-ins' as const, label: 'Plugin sign-ins', icon: Laptop, show: true },
    { key: 'policies' as const, label: 'Policies', icon: ShieldCheck, show: isIT },
    { key: 'org' as const, label: 'Organization', icon: Building2, show: isAdmin },
    { key: 'identity' as const, label: 'Identity', icon: KeyRound, show: isAdmin },
    { key: 'scm' as const, label: 'Source control', icon: GitBranch, show: isAdmin },
    { key: 'notifications' as const, label: 'Notifications', icon: Bell, show: isIT },
    { key: 'app-lifecycle' as const, label: 'Unused apps', icon: Hourglass, show: isIT },
    { key: 'billing' as const, label: 'Billing', icon: CreditCard, show: isAdmin },
    { key: 'maintenance' as const, label: 'Maintenance', icon: Wrench, show: isAdmin },
    { key: 'danger' as const, label: 'Danger zone', icon: AlertTriangle, show: isAdmin },
  ];
  const items: SettingsNavItem[] = ALL_SECTIONS.filter((s) => s.show).map(
    ({ key, label, icon }) => ({ key, label, icon }),
  );

  const validKeys = React.useMemo(() => {
    const keys = new Set<SettingsSectionKey>(['agent', 'sign-ins']);
    if (isAdmin) {
      for (const key of ['org', 'identity', 'scm', 'billing', 'maintenance', 'danger'] as const) {
        keys.add(key);
      }
    }
    if (isIT) {
      keys.add('notifications');
      keys.add('policies');
      keys.add('app-lifecycle');
    }
    return keys;
  }, [isAdmin, isIT]);

  const tabParam = searchParams.get('tab') as SettingsSectionKey | null;
  const active: SettingsSectionKey =
    tabParam && validKeys.has(tabParam) ? tabParam : (items[0]?.key ?? 'agent');

  React.useEffect(() => {
    if (tabParam === null || validKeys.has(tabParam)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', active);
        return next;
      },
      { replace: true },
    );
  }, [tabParam, active, validKeys, setSearchParams]);

  const setActive = (key: SettingsSectionKey): void => {
    const p = new URLSearchParams(searchParams);
    p.set('tab', key);
    p.delete('focus');
    setSearchParams(p, { replace: true });
  };

  function renderActiveSection(): React.ReactNode {
    if (!settings) return null;
    switch (active) {
      case 'agent':
        return <AgentSetup agentSetup={settings.agent_setup} userRole={user?.role} />;
      case 'sign-ins':
        return <PluginSignIns userRole={user?.role} />;
      case 'policies':
        return <PoliciesSettings />;
      case 'billing':
        return <BillingSettings />;
      case 'org':
        return (
          <OrgSettings
            org={settings.org}
            onSaveDisplayName={onSaveDisplayName}
            onSaveContact={onSaveContact}
            onToggleDiscoverabilityDefault={onToggleDiscoverabilityDefault}
            onToggleFeedbackAutoShare={onToggleFeedbackAutoShare}
            onToggleCrashLogAutoShare={onToggleCrashLogAutoShare}
            onToggleObserverReports={onToggleObserverReports}
            onToggleTokenUsageReports={onToggleTokenUsageReports}
            onSetCursorAdminKey={onSetCursorAdminKey}
            onRemoveCursorAdminKey={onRemoveCursorAdminKey}
          />
        );
      case 'identity':
        return (
          <IdentitySettings
            identity={settings.identity}
            roleSource={settings.role_source}
            onTestConnection={onTestConnection}
            onVerifyIdp={onVerifyIdp}
            onReconfigure={onReconfigure}
            onToggleGroupNameResolution={onToggleGroupNameResolution}
          />
        );
      case 'scm':
        return (
          <div className="space-y-6">
            <SourceControlSettings
              sourceControl={settings.source_control}
              onOverrideScanner={(name) =>
                toast.info(`Scanner override for ${name} is coming soon.`)
              }
              onVerify={onVerifyRepoAccess}
              onRecreate={onRecreateGithubApp}
              onPreviewMigration={onPreviewMigration}
              onExecuteMigration={onExecuteMigration}
            />
            <MarketplaceCard
              marketplace={settings.marketplace}
              orgHandle={settings.source_control?.account ?? null}
              onBootstrap={onBootstrapMarketplace}
              onUnregister={onUnregisterMarketplace}
              focusCreate={searchParams.get('focus') === 'marketplace'}
            />
          </div>
        );
      case 'notifications':
        return (
          <NotificationsSettings
            notifications={settings.notifications}
            onUpdate={onUpdate}
            onSetSlackWebhook={onSetSlackWebhook}
            onRemoveSlackWebhook={onRemoveSlackWebhook}
          />
        );
      case 'app-lifecycle':
        return (
          <AppLifecycleSettings settings={settings.app_lifecycle} onUpdate={onUpdateAppLifecycle} />
        );
      case 'maintenance':
        return <MaintenanceSettings />;
      case 'danger':
        return <DangerZone runbookUrl={DOCS_LINKS.uninstall} />;
      default:
        return null;
    }
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {isError ? (
        <>
          <PageHeader title="Settings" />
          <Alert variant="destructive" data-testid="settings-error">
            <AlertTitle>Couldn&apos;t load settings.</AlertTitle>
            <AlertDescription>
              Something went wrong fetching your settings. Retry in a moment.
            </AlertDescription>
          </Alert>
        </>
      ) : isLoading ? (
        <>
          <PageHeader title="Settings" />
          <Skeleton className="h-40 rounded-gl-lg" />
          <Skeleton className="h-40 rounded-gl-lg" />
        </>
      ) : !settings ? (
        <>
          <PageHeader title="Settings" />
          <Alert variant="destructive" data-testid="settings-error">
            <AlertTitle>Couldn&apos;t load settings.</AlertTitle>
            <AlertDescription>
              Something went wrong fetching your settings. Retry in a moment.
            </AlertDescription>
          </Alert>
        </>
      ) : (
        <>
          <PageHeader eyebrow={settings?.org?.display_name} title="Settings" />
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-10">
            <aside className="lg:w-56 lg:shrink-0">
              <SettingsNav items={items} active={active} onSelect={setActive} />
            </aside>
            <div className="min-w-0 flex-1 lg:max-w-[680px]">{renderActiveSection()}</div>
          </div>
        </>
      )}
    </div>
  );
}
